/**
 * useStagingConfig — the staging module's ops hook (the ONLY store write path).
 *
 * MODULE-CONTRACT §2 + §10. The module's durable state IS the `StagingDraftDTO`
 * at `productConfigs["${cartItemId}|staging"]`; this hook wraps it with a typed
 * read + the room mutators. No imperative handle, no serialize/hydrate —
 * `serializeDraft` / `hydrateFromDraft` already round-trip `productConfigs`.
 *
 * ⚠️ VALUE-GUARD (MODULE-CONTRACT §10.2 / gate G1): `markConfigDirty` is
 * VALUE-BLIND (`productConfigSlice.ts:126-136` un-confirms on the flag alone,
 * never the DTO value). So `addRoom`/`removeRoom` MUST be STRUCTURALLY IDEMPOTENT
 * — they early-return BEFORE `writeRooms`/`markConfigDirty` when the key is already
 * present / already absent. That membership diff (NOT `setConfig`'s JSON-eq) is
 * the churn guard: an app-wide `FileAPIProvider` re-fire (a foreign-scope upload
 * hands the projection a fresh array ref) with unchanged membership never reaches
 * `markConfigDirty`, so a Saved staging config stays confirmed.
 */

import { useCallback } from 'react'

import type { CartItemId } from 'models/purchaseFlowRevamp'

import { usePfStore } from '../../store'
import { makeConfigKey } from '../../store/slices/productConfigSlice'
import { normalizeRoom, stagingIsComplete, type StagingDraftDTO, type StagingDraftRoomDTO } from './staging.dto'
import { hasRoomSelectedAllTemplates, isRoomCompleted } from './stagingRoom.logic'

/** Stable empty-rooms reference so consumers don't re-render on `{}` identity churn. */
const EMPTY_ROOMS: Record<string, StagingDraftRoomDTO> = {}

export interface UseStagingConfig {
  /** The persisted room map (stable identity until a real write). */
  rooms: Record<string, StagingDraftRoomDTO>
  /** Merge a partial into one room + recompute its completeness snapshots. */
  patchRoom: (roomKey: string, partial: Partial<StagingDraftRoomDTO>) => void
  /** Add a room (idempotent — no-op if the key already exists). */
  addRoom: (roomKey: string, room: StagingDraftRoomDTO) => void
  /** Remove a room (idempotent — no-op if the key is already absent). */
  removeRoom: (roomKey: string) => void
  isComplete: boolean
}

export function useStagingConfig(cartItemId: CartItemId): UseStagingConfig {
  const key = makeConfigKey(cartItemId, 'staging')

  // Fine-grained subscription: re-renders only when THIS item's DTO changes.
  const raw = usePfStore((s) => s.productConfigs[key] as StagingDraftDTO | undefined)
  const rooms = raw?.rooms ?? EMPTY_ROOMS

  // The single write primitive. Only ever reached on a REAL change (guarded by
  // the structural early-returns below + `patchRoom`'s "room exists" check), so
  // the value-blind `markConfigDirty` never spuriously un-confirms.
  const writeRooms = useCallback(
    (nextRooms: Record<string, StagingDraftRoomDTO>) => {
      const s = usePfStore.getState()
      s.patchConfig(cartItemId, 'staging', { rooms: nextRooms })  // Un-confirms only on a real change
    },
    [cartItemId]
  )

  const patchRoom = useCallback(
    (roomKey: string, partial: Partial<StagingDraftRoomDTO>) => {
      const cur = usePfStore.getState().productConfigs[key] as StagingDraftDTO | undefined
      const room = cur?.rooms[roomKey]
      if (!room) return

      // Field-merge over the default (partial-DTO invariant) so the recompute
      // below never reads an undefined array off a sparse hydrated room.
      const merged: StagingDraftRoomDTO = normalizeRoom(roomKey, { ...room, ...partial })
      writeRooms({
        ...cur!.rooms,
        [roomKey]: {
          ...merged,
          // Recompute snapshots exactly like the legacy UPDATE_ROOM reducer.
          isComplete: isRoomCompleted(merged),
          hasSelectedAllStyleCodes: hasRoomSelectedAllTemplates(merged),
        },
      })
    },
    [key, writeRooms]
  )

  // Structurally idempotent ADD: never write on unchanged membership.
  const addRoom = useCallback(
    (roomKey: string, room: StagingDraftRoomDTO) => {
      const cur = usePfStore.getState().productConfigs[key] as StagingDraftDTO | undefined
      if (cur?.rooms[roomKey]) return // already present → no-op
      writeRooms({ ...(cur?.rooms ?? {}), [roomKey]: room })
    },
    [key, writeRooms]
  )

  // Structurally idempotent REMOVE: never write when already absent.
  const removeRoom = useCallback(
    (roomKey: string) => {
      const cur = usePfStore.getState().productConfigs[key] as StagingDraftDTO | undefined
      if (!cur?.rooms[roomKey]) return // already absent → no-op
      const { [roomKey]: _drop, ...rest } = cur.rooms
      writeRooms(rest)
    },
    [key, writeRooms]
  )

  return { rooms, patchRoom, addRoom, removeRoom, isComplete: stagingIsComplete(raw) }
}
